// Internal P&L as PDF and Excel
import { CUR, grandTotal, secTotals } from '../core/budget.js';
import { h, useEffect, useRef, useState } from '../core/react.js';
import { T, fmt } from '../core/theme.js';
import { estRegisterFont } from './estimate-pdf.js';
import { audit } from '../core/audit.js';
import { loadExcelJS } from '../core/excel.js';
import { saveBlobFile } from '../core/download.js';
// whole amounts, no symbol (the column says the currency)
const money = n => n == null || !isFinite(n) ? '–' : (n < 0 ? '-' : '') + Math.round(Math.abs(n)).toLocaleString('en-GB');

// ── Internal P&L: a real PDF table (landscape A4), not a screenshot of the screen
export function buildInternalPDF({ project, sections, fx, user }) {
  const { jsPDF } = window.jspdf;
  const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'landscape', compress: true });
  estRegisterFont(doc);
  const W = doc.internal.pageSize.getWidth(), H = doc.internal.pageSize.getHeight(), M = 28;
  const cost = CUR.cost, bill = CUR.bill;
  const NAVY = [62, 63, 67], SLATE = [78, 80, 86], MIST = [138, 140, 145], RULE = [217, 218, 221], SEC = [237, 237, 239], SUB = [246, 246, 247];
  const RED = [192, 57, 43], GREEN = [26, 110, 58], AMBER = [181, 96, 10];
  const font = (size, bold, color = [30, 30, 30]) => { doc.setFont('EstSans', bold ? 'bold' : 'normal'); doc.setFontSize(size); doc.setTextColor(...color); };
  const pct = n => n == null || !isFinite(n) ? '–' : (n * 100).toFixed(1) + '%';
  const COLS = [
    { k: 'ln', h: '#', w: 18 },
    { k: 'desc', h: 'Description', w: 150 },
    { k: 'sup', h: 'Supplier', w: 80 },
    { k: 'st', h: 'Status', w: 52 },
    { k: 'ccy', h: 'CCY', w: 30 },
    { k: 'qty', h: 'Qty', w: 34, r: true },
    { k: 'up', h: 'Unit price', w: 54, r: true },
    { k: 'in', h: `In-cost ${cost}`, w: 62, r: true },
    { k: 'mu', h: 'Markup', w: 34, r: true },
    { k: 'out', h: `Out-cost ${bill}`, w: 66, r: true },
    { k: 'mg', h: `Margin ${bill}`, w: 60, r: true },
    { k: 'mp', h: 'Margin %', w: 36, r: true },
    { k: 'act', h: `Actual ${cost}`, w: 62, r: true },
    { k: 'var', h: `Variance ${cost}`, w: 50, r: true }
  ];
  const scale = (W - 2 * M) / COLS.reduce((s, c) => s + c.w, 0);
  let x = M; COLS.forEach(c => { c.w *= scale; c.x = x; x += c.w; });
  const gt = grandTotal(sections, fx);
  const prepared = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  let y = 0;

  const footer = () => {
    font(6.5, false, MIST);
    doc.text(`${project?.name || 'Budget'} · Internal P&L · Confidential, not for clients`, M, H - 16);
  };
  const tableHead = () => {
    doc.setFillColor(...NAVY); doc.rect(M, y, W - 2 * M, 16, 'F');
    font(6.8, true, [255, 255, 255]);
    COLS.forEach(c => doc.text(c.h, c.r ? c.x + c.w - 4 : c.x + 4, y + 10.5, c.r ? { align: 'right' } : undefined));
    y += 16;
  };
  const newPage = first => {
    if (!first) { footer(); doc.addPage('a4', 'landscape'); }
    y = M;
    if (first) {
      font(7, true, MIST); doc.text('INTERNAL P&L', M, y + 6);
      font(15, true, [30, 30, 30]); doc.text(project?.name || 'Budget', M, y + 24);
      font(8, false, SLATE);
      doc.text([project?.client, project?.date, project?.status].filter(Boolean).join('  ·  ') || ' ', M, y + 37);
      const right = [`Costs in ${cost} · client billed in ${bill}`,
        project?.fxLocked ? `Rates locked ${project.fxLockedAt || ''}${project.fxLockedBy ? ' by ' + project.fxLockedBy : ''}` : 'Rates follow the team table (not locked)',
        `Prepared ${prepared}${user?.name ? ' by ' + user.name : ''}`];
      font(7.5, false, SLATE); right.forEach((t, i) => doc.text(t, W - M, y + 8 + i * 11, { align: 'right' }));
      y += 50;
      // summary strip
      const act = gt.actInGBP, varv = gt.varGBP;
      const boxes = [[`In-cost (${cost})`, money(gt.inGBP)], [`Out-cost (${bill})`, money(gt.outUSD)], [`Margin (${bill})`, money(gt.margUSD)],
        ['Margin %', pct(gt.outUSD > 0 ? gt.margUSD / gt.outUSD : null)], [`Actual to date (${cost})`, money(act)], [`Variance (${cost})`, money(varv)]];
      const bw = (W - 2 * M - 5 * 6) / 6;
      boxes.forEach(([l, v], i) => {
        const bx = M + i * (bw + 6);
        doc.setDrawColor(...RULE); doc.setLineWidth(0.6); doc.rect(bx, y, bw, 34);
        font(6.5, false, MIST); doc.text(l, bx + 7, y + 11);
        font(11, true, i === 5 && varv != null ? (varv > 0 ? RED : GREEN) : [30, 30, 30]); doc.text(v, bx + 7, y + 27);
      });
      y += 46;
      if (gt.missing && gt.missing.length) {
        font(7.5, true, RED); doc.text(`Lines in ${gt.missing.join(', ')} have no exchange rate and are counted as zero.`, M, y); y += 12;
      }
    }
    tableHead();
  };
  const need = h => { if (y + h > H - 30) newPage(false); };
  newPage(true);

  sections.forEach(sec => {
    const st = secTotals(sec.items || [], fx);
    need(16 + 13);
    doc.setFillColor(...SEC); doc.rect(M, y, W - 2 * M, 14, 'F');
    font(7.5, true, NAVY); doc.text(String(sec.name || '').toUpperCase(), M + 4, y + 9.5);
    y += 14;
    st.items.forEach(ci => {
      const descLines = doc.splitTextToSize(String(ci.desc || ''), COLS[1].w - 8).slice(0, 2);
      const supLines = doc.splitTextToSize(String(ci.sup || ''), COLS[2].w - 8).slice(0, 2);
      const rh = 6 + 9 * Math.max(1, descLines.length, supLines.length);
      need(rh);
      const cancelled = ci.st === 'Cancelled';
      const vals = {
        ln: String(ci.ln ?? ''), desc: descLines, sup: supLines, st: String(ci.st || ''), ccy: ci.noRate ? `${ci.ccy}?` : String(ci.ccy || ''),
        qty: fmt.num(ci.qty, Number.isInteger(+ci.qty) ? 0 : 2), up: fmt.num(ci.up, 2), in: money(ci.inGBP), mu: pct(ci.mu),
        out: money(ci.outUSD), mg: money(ci.margUSD), mp: pct(ci.outUSD > 0 ? ci.margUSD / ci.outUSD : null),
        act: ci.actInGBP == null ? '' : money(ci.actInGBP), var: ci.varGBP == null ? '' : money(ci.varGBP)
      };
      COLS.forEach(c => {
        let color = cancelled ? MIST : c.k === 'var' && ci.varGBP != null ? (ci.varGBP > 0.5 ? RED : GREEN) : c.k === 'ccy' && ci.noRate ? AMBER : [30, 30, 30];
        font(7.2, c.k === 'out', color);
        const v = vals[c.k];
        doc.text(v, c.r ? c.x + c.w - 4 : c.x + 4, y + 9.5, c.r ? { align: 'right' } : undefined);
      });
      doc.setDrawColor(...RULE); doc.setLineWidth(0.3); doc.line(M, y + rh, W - M, y + rh);
      y += rh;
    });
    need(14);
    doc.setFillColor(...SUB); doc.rect(M, y, W - 2 * M, 13, 'F');
    const sub = { desc: 'Subtotal', in: money(st.inGBP), out: money(st.outUSD), mg: money(st.margUSD), mp: pct(st.outUSD > 0 ? st.margUSD / st.outUSD : null),
      act: st.actInGBP == null ? '' : money(st.actInGBP), var: st.varGBP == null ? '' : money(st.varGBP) };
    COLS.forEach(c => { if (sub[c.k] == null) return; font(7.2, true, c.k === 'var' && st.varGBP != null ? (st.varGBP > 0.5 ? RED : GREEN) : NAVY); doc.text(sub[c.k], c.r ? c.x + c.w - 4 : c.x + 4, y + 9, c.r ? { align: 'right' } : undefined); });
    y += 19;
  });
  need(18);
  doc.setFillColor(...NAVY); doc.rect(M, y, W - 2 * M, 16, 'F');
  const tot = { desc: 'Grand total', in: money(gt.inGBP), out: money(gt.outUSD), mg: money(gt.margUSD), mp: pct(gt.outUSD > 0 ? gt.margUSD / gt.outUSD : null),
    act: gt.actInGBP == null ? '' : money(gt.actInGBP), var: gt.varGBP == null ? '' : money(gt.varGBP) };
  COLS.forEach(c => { if (tot[c.k] == null) return; font(7.8, true, [255, 255, 255]); doc.text(tot[c.k], c.r ? c.x + c.w - 4 : c.x + 4, y + 10.8, c.r ? { align: 'right' } : undefined); });
  footer();
  const n = doc.getNumberOfPages();
  for (let i = 1; i <= n; i++) { doc.setPage(i); font(6.5, false, MIST); doc.text(`Page ${i} of ${n}`, W - M, H - 16, { align: 'right' }); }
  doc.setProperties({ title: `${project?.name || 'Budget'} – Internal P&L` });
  return doc;
}
// On-screen preview of the internal report, drawn as HTML (PDF viewers don't run inside every frame).
// Same content and order as buildInternalPDF.
// ── Internal P&L as an Excel workbook: same rows as the PDF, totals as live formulas
// The Internal P&L as a workbook: the same Glassbox budget workbook, in the currencies shown on the page
export async function exportInternalXlsx({ project, sections, fx, user }) {
  const [ExcelJS, { buildBudgetWorkbook }, kit] = await Promise.all([loadExcelJS(), import('../core/xlsx/budget-xlsx.js'), import('../core/xlsx/kit.js')]);
  const wb = buildBudgetWorkbook(ExcelJS, { project: { ...project, sections }, user, fx, ccy: { cost: CUR.cost, bill: CUR.bill }, kicker: 'Internal P&L · confidential' });
  audit('pnl.xlsx', { project: project?.name });
  return saveBlobFile(await kit.workbookBlob(wb), `${kit.fileSafe(project?.name || 'Budget')} - Internal P&L ${new Date().toISOString().slice(0, 10)}.xlsx`);
}

export function InternalPreviewSheet({ project, sections, fx, user }) {
  // The sheet is laid out at 1060px (like the landscape page) and zoomed to fit the space available.
  const box = useRef(null);
  const [zoom, setZoom] = useState(1);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const measure = () => setZoom(Math.max(0.4, Math.min(1, (el.clientWidth - 56) / 1060)));
    measure();
    const ro = window.ResizeObserver ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el); else window.addEventListener('resize', measure);
    return () => { if (ro) ro.disconnect(); else window.removeEventListener('resize', measure); };
  }, []);
  const gt = grandTotal(sections, fx);
  const cost = CUR.cost, bill = CUR.bill;
  const pct = n => n == null || !isFinite(n) ? '–' : (n * 100).toFixed(1) + '%';
  const RED = T.red, GREEN = T.green;
  const cols = [['#', 'l'], ['Description', 'l'], ['Supplier', 'l'], ['Status', 'l'], ['CCY', 'l'], ['Qty', 'r'], ['Unit price', 'r'], [`In-cost ${cost}`, 'r'], ['Markup', 'r'],
    [`Out-cost ${bill}`, 'r'], [`Margin ${bill}`, 'r'], ['Margin %', 'r'], [`Actual ${cost}`, 'r'], [`Variance ${cost}`, 'r']];
  const td = (v, al, extra = {}) => h("td", { style: { padding: '4px 6px', textAlign: al === 'r' ? 'right' : 'left', borderBottom: `1px solid ${T.rule}`, whiteSpace: al === 'r' ? 'nowrap' : 'normal', ...extra } }, v);
  const varColor = v => v == null ? undefined : v > 0.5 ? RED : GREEN;
  const boxes = [[`In-cost (${cost})`, money(gt.inGBP)], [`Out-cost (${bill})`, money(gt.outUSD)], [`Margin (${bill})`, money(gt.margUSD)],
    ['Margin %', pct(gt.outUSD > 0 ? gt.margUSD / gt.outUSD : null)], [`Actual to date (${cost})`, money(gt.actInGBP)], [`Variance (${cost})`, money(gt.varGBP)]];
  return h("div", { ref: box, style: { flex: 1, minWidth: 0, overflow: 'auto', padding: 28, background: T.ivoryD } },
    h("div", { style: { width: 1060, zoom, margin: '0 auto', background: T.white, padding: '28px 30px 22px', boxShadow: '0 4px 24px rgba(62,63,67,.14)', fontSize: 10.5, color: T.coal } },
      h("div", { style: { display: 'flex', justifyContent: 'space-between', marginBottom: 14 } },
        h("div", null,
          h("div", { style: { fontSize: 9, fontWeight: 700, color: T.mist, letterSpacing: '.06em' } }, 'INTERNAL P&L'),
          h("div", { style: { fontSize: 19, fontWeight: 700, margin: '3px 0 2px' } }, project?.name || 'Budget'),
          h("div", { style: { color: T.slate } }, [project?.client, project?.date, project?.status].filter(Boolean).join('  ·  '))),
        h("div", { style: { textAlign: 'right', color: T.slate, lineHeight: 1.6 } },
          h("div", null, `Costs in ${cost} · client billed in ${bill}`),
          h("div", null, project?.fxLocked ? `Rates locked ${project.fxLockedAt || ''}${project.fxLockedBy ? ' by ' + project.fxLockedBy : ''}` : 'Rates follow the team table (not locked)'),
          h("div", null, `Prepared ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}${user?.name ? ' by ' + user.name : ''}`))),
      h("div", { style: { display: 'grid', gridTemplateColumns: 'repeat(6,1fr)', gap: 6, marginBottom: 14 } }, boxes.map(([l, v], i) =>
        h("div", { key: l, style: { border: `1px solid ${T.rule}`, padding: '6px 9px' } },
          h("div", { style: { fontSize: 9, color: T.mist } }, l),
          h("div", { style: { fontSize: 14, fontWeight: 700, marginTop: 2, color: i === 5 ? varColor(gt.varGBP) : T.coal } }, v)))),
      gt.missing && gt.missing.length > 0 && h("div", { style: { color: RED, fontWeight: 600, marginBottom: 8 } }, `Lines in ${gt.missing.join(', ')} have no exchange rate and are counted as zero.`),
      h("table", { style: { width: '100%', borderCollapse: 'collapse', fontSize: 10 } },
        h("thead", null, h("tr", { style: { background: T.navy, color: T.white } }, cols.map(([t, al]) => h("th", { key: t, style: { padding: '6px', textAlign: al === 'r' ? 'right' : 'left', fontWeight: 700, fontSize: 9, whiteSpace: 'nowrap' } }, t)))),
        h("tbody", null, sections.map(sec => {
          const st = secTotals(sec.items || [], fx);
          return [h("tr", { key: sec.id + 'h', style: { background: T.ivoryD } }, h("td", { colSpan: cols.length, style: { padding: '5px 6px', fontWeight: 700, color: T.navy, fontSize: 9.5 } }, String(sec.name || '').toUpperCase())),
            ...st.items.map(ci => {
              const dim = ci.st === 'Cancelled' ? { color: T.mist } : {};
              return h("tr", { key: ci.id, style: dim }, td(ci.ln, 'l'), td(ci.desc, 'l'), td(ci.sup || '', 'l'), td(ci.st || '', 'l'),
                td(ci.noRate ? ci.ccy + '?' : ci.ccy, 'l', ci.noRate ? { color: T.amber, fontWeight: 600 } : {}), td(fmt.num(ci.qty, Number.isInteger(+ci.qty) ? 0 : 2), 'r'), td(fmt.num(ci.up, 2), 'r'),
                td(money(ci.inGBP), 'r'), td(pct(ci.mu), 'r'), td(money(ci.outUSD), 'r', { fontWeight: 600 }), td(money(ci.margUSD), 'r'),
                td(pct(ci.outUSD > 0 ? ci.margUSD / ci.outUSD : null), 'r'), td(ci.actInGBP == null ? '' : money(ci.actInGBP), 'r'),
                td(ci.varGBP == null ? '' : money(ci.varGBP), 'r', { color: varColor(ci.varGBP) }));
            }),
            h("tr", { key: sec.id + 's', style: { background: T.ivory, fontWeight: 700 } }, td('', 'l'), td('Subtotal', 'l'), td('', 'l'), td('', 'l'), td('', 'l'), td('', 'r'), td('', 'r'),
              td(money(st.inGBP), 'r'), td('', 'r'), td(money(st.outUSD), 'r'), td(money(st.margUSD), 'r'), td(pct(st.outUSD > 0 ? st.margUSD / st.outUSD : null), 'r'),
              td(st.actInGBP == null ? '' : money(st.actInGBP), 'r'), td(st.varGBP == null ? '' : money(st.varGBP), 'r', { color: varColor(st.varGBP) }))];
        }),
          h("tr", { style: { background: T.navy, color: T.white, fontWeight: 700 } }, td('', 'l', { borderBottom: 'none' }), td('Grand total', 'l', { borderBottom: 'none' }), ...[0, 1, 2, 3, 4].map(i => td('', 'l', { borderBottom: 'none' })),
            td(money(gt.inGBP), 'r', { borderBottom: 'none' }), td('', 'r', { borderBottom: 'none' }), td(money(gt.outUSD), 'r', { borderBottom: 'none' }), td(money(gt.margUSD), 'r', { borderBottom: 'none' }),
            td(pct(gt.outUSD > 0 ? gt.margUSD / gt.outUSD : null), 'r', { borderBottom: 'none' }), td(gt.actInGBP == null ? '' : money(gt.actInGBP), 'r', { borderBottom: 'none' }), td(gt.varGBP == null ? '' : money(gt.varGBP), 'r', { borderBottom: 'none' })))),
      h("div", { style: { marginTop: 14, fontSize: 9, color: T.mist } }, 'Confidential, not for clients. The download is a landscape A4 PDF with this content.')));
}
